import { Download, Trash2, FileUp, X } from "lucide-react";
import { Button } from "../primitives/Button";
import { Dialog, DialogClose, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { cn } from "../lib/cn";

/** The file a {@link FileOverlay} previews. */
export interface FileOverlayFile {
  /** File name, shown as the title and the dialog's accessible label. */
  name: string;
  /** `"image"` renders `previewUrl` as an `<img>`; `"document"` renders a placeholder card. */
  kind: "image" | "document";
  /** Image source (object URL or remote URL). Required for `kind: "image"`. */
  previewUrl?: string;
  /** Size in bytes, shown on the document card when known. */
  sizeBytes?: number;
}

export interface FileOverlayProps {
  /** Whether the overlay is open (controlled). */
  open: boolean;
  /** Fires when the overlay requests close (scrim, Escape, or the close button). */
  onOpenChange: (open: boolean) => void;
  /** The file to preview. */
  file: FileOverlayFile;
  /** Download handler. When omitted, the download action is hidden. */
  onDownload?: () => void;
  /**
   * Delete handler. When provided a Delete action is shown (the composer /
   * not-yet-sent variant); omit it for a read-only "sent file" preview.
   */
  onDelete?: () => void;
}

const fileExtension = (name: string): string => name.split(".").pop()?.toUpperCase() || "FILE";

const formatSize = (bytes?: number): string | null => {
  if (bytes == null || bytes < 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * FileOverlay — a full-screen preview lightbox for a chat attachment (ported
 * library-clean from the AI-Expert FileOverlay / SentFileOverlay, unified into
 * one component).
 *
 * Renders an image full-bleed, or a document as a labelled card. Built on the
 * `Dialog` primitive, so it inherits focus-trap, Escape-to-close, scroll-lock,
 * and the portal for free (the source overlay had none of these). Pass
 * `onDelete` for the editable "composer" variant; omit it for the read-only
 * "sent" variant.
 *
 * Layout follows the PhotoSwipe / medium-zoom lightbox convention: controls
 * bind to the *media* box, not the wide transparent dialog box. The close X
 * sits at the top-right corner of the image / card itself, and the download /
 * delete actions sit in a centered bar directly below it — so they always hug
 * the rendered artwork instead of floating out in the dialog's empty gutters.
 * Brand tokens only.
 */
export function FileOverlay({ open, onOpenChange, file, onDownload, onDelete }: FileOverlayProps) {
  const size = formatSize(file.sizeBytes);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // Hide the built-in top-right X; we render our own re-anchored to the
        // media figure (Radix Root still owns Escape + scrim close).
        showCloseButton={false}
        className={cn(
          // Strip the default modal chrome for a full-bleed lightbox; cn (twMerge)
          // resolves these over DialogContent's defaults (max-w-lg / p-6 / border / bg).
          // `flex flex-col items-center` centers the shrink-to-content figure and the
          // action bar that follows it.
          "flex max-w-[min(95vw,72rem)] flex-col items-center border-0 bg-transparent p-0 shadow-none sm:max-w-[min(95vw,72rem)]",
        )}
      >
        <DialogTitle className="sr-only">{file.name}</DialogTitle>
        <DialogDescription className="sr-only">Attachment preview</DialogDescription>

        {/*
          Figure wrapper: `relative w-fit max-w-full` shrinks to the rendered
          media's width, so the absolutely-positioned close X tracks the image /
          card's real edge instead of the wide dialog box.
        */}
        <div className="relative w-fit max-w-full">
          {file.kind === "image" && file.previewUrl ? (
            <img
              src={file.previewUrl}
              alt={file.name}
              className="block max-h-[85vh] max-w-full rounded-lg object-contain"
            />
          ) : (
            <div className="flex min-w-[min(90vw,24rem)] flex-col items-center justify-center rounded-lg bg-card p-12 text-center">
              <FileUp className="mb-4 size-16 text-muted-foreground" />
              <h3 className="mb-2 text-lg font-semibold text-foreground">{file.name}</h3>
              <p className="text-sm text-muted-foreground">
                {fileExtension(file.name)}
                {size ? ` · ${size}` : ""}
              </p>
            </div>
          )}

          <DialogClose asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Close"
              className="absolute top-2 right-2 rounded-full bg-background/70 backdrop-blur"
            >
              <X className="size-4" />
            </Button>
          </DialogClose>
        </div>

        {(onDownload || onDelete) && (
          <div className="mt-3 flex items-center justify-center gap-2">
            {onDownload && (
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="rounded-full bg-background/80 backdrop-blur"
                onClick={onDownload}
                aria-label="Download file"
              >
                <Download className="size-4" />
              </Button>
            )}
            {onDelete && (
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="rounded-full bg-background/80 backdrop-blur"
                onClick={onDelete}
                aria-label="Delete file"
              >
                <Trash2 className="size-4 text-destructive" />
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
